import { ReactNode, useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { FunctionReference } from "convex/server";
import { Edit, Eye, Trash, type LucideIcon } from "lucide-react";
import { useTableData } from "./useTableData";
import { useTableNavigation } from "./table/useTableNavigation";
import { useModal, useDeleteModal } from "./useModal";
import { usePermissions } from "@/hooks/usePermissions";

import { Filter, HeaderAction } from "@/components/table/table-filters";
export type { HeaderAction } from "@/components/table/table-filters";
import DataTable from "@/components/table/data-table";
import { TableFilters } from "@/components/table/table-filters";
import { DeleteConfirmationModal } from "@/components/ui/modal/delete-confirmation-modal";
import { TableColumnGeneric } from "./table/types";
import { IconName } from "@/lib/modules";
import { modulesTypes } from "@/convex/system/permissions";

export interface TableModalProps<TData> {
  isOpen: boolean;
  data?: TData;
  onClose: () => void;
  onSuccess: () => void;
}

export interface TableConfig<TData> {
  tableName: typeof modulesTypes.type;
  query: FunctionReference<"query">;
  title: string | (() => string);
  icon?: IconName;
  description: string | (() => string);
  columns: (helpers: TableHelpers<TData>) => TableColumnGeneric<TData>[];
  filters?: (
    helpers: TableHelpers<TData>,
    customHooksData: Record<string, unknown>,
  ) => Filter[];
  actions?: (
    helpers: TableActionHelpers<TData>,
    customHooksData: Record<string, unknown>,
  ) => TableActions<TData>;
  modal?: {
    component?: React.ComponentType<TableModalProps<TData>>;
    getItemName?: (item: TData) => string;
  };
  customHooks?: (
    data: TData[],
    helpers: TableHelpers<TData>,
  ) => Record<string, any>;
  createAction?: "modal" | "route" | "none";
  createRoute?: string;
  headerActions?: (customHooksData: Record<string, unknown>) => HeaderAction[];
}

export interface TableHelpers<TData> {
  navigate: ReturnType<typeof useNavigate>;
  formatCurrency: (amount: number, currency?: string) => string;
  formatDate: (date: number | Date) => string;
}

export interface TableActionHelpers<TData> extends TableHelpers<TData> {
  modal: ReturnType<typeof useModal<TData>>;
  deleteModal: ReturnType<typeof useDeleteModal<TData>>;
}

export interface TableActions<TData> {
  onView?: (item: TData) => void;
  onEdit?: (item: TData) => void;
  onDelete?: (
    item: TData,
    options?: { skipModal?: boolean },
  ) => void | Promise<void>;
  deleteCondition?: (item: TData) => boolean;
  customActions?: Array<{
    label: string;
    icon: LucideIcon;
    onClick: (item: TData) => void;
    variant?: "default" | "ghost" | "outline";
    condition?: (item: TData) => boolean;
    permission?: "read" | "create" | "update" | "delete";
  }>;
}

/**
 * Parámetros de búsqueda base que son comunes en todas las tablas.
 * - page: número de página (paginación)
 * - search: término de búsqueda general
 * - status: filtro de estado (común en la mayoría de tablas)
 */
export interface BaseSearchParams {
  page?: number;
  search?: string;
  status?: string;
}

/**
 * Props genéricas para componentes de tabla.
 * Reutilizable en todos los componentes de tabla para evitar duplicación.
 * Incluye searchParams base por defecto (page, search, status).
 *
 * @example
 * // Solo con searchParams base
 * interface InvoicesTableProps extends TableProps {}
 *
 * @example
 * // Con searchParams adicionales específicos
 * interface ProductsTableProps extends TableProps<
 *   BaseSearchParams & {
 *     category?: string;
 *     inStock?: boolean;
 *   }
 * > {}
 */
export interface TableProps<
  TSearchParams extends Record<string, any> = BaseSearchParams,
> {
  currentPage: number;
  pageSize: number;
  basePath: string;
  searchParams?: TSearchParams;
}

export interface UseTableOptions {
  currentPage: number;
  pageSize: number;
  basePath: string;
  searchParams?: Record<string, any>;
}

export interface UseTableReturn<TData> {
  // Data
  data: TData[];
  isLoading: boolean;
  isError: boolean;

  // Navigation
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;

  // Modals
  modal: ReturnType<typeof useModal<TData>>;
  deleteModal: ReturnType<typeof useDeleteModal<TData>>;

  // Custom hooks results
  customHooksData: Record<string, any>;

  // Render helpers
  renderTable: () => ReactNode;
  renderModals: () => ReactNode;
  render: (options?: { extraContent?: ReactNode }) => ReactNode;
}

/**
 * Factory para crear hooks de tabla específicos
 *
 * PATRÓN RECOMENDADO: Definir la configuración dentro del mismo archivo del componente
 * para mantener la co-localización y evitar proliferar archivos de hooks.
 *
 * @example
 * // Define el tipo de datos de tu tabla
 * type InvoiceData = Doc<"invoices"> & {
 *   client: Doc<"clients">;
 *   seller: Doc<"sellers">;
 *   lastUpdatedBy: Doc<"users">;
 * };
 *
 * // Crea el hook usando el factory (antes del componente, a nivel de módulo)
 * const useInvoicesTable = createTableHook<InvoiceData>({
 *   tableName: "invoices",
 *   query: api.companies.invoices.queries.getInvoicesOptimized,
 *   title: "Facturas",
 *   description: "Tabla de Facturas",
 *
 *   // Columnas con helpers tipados
 *   columns: (helpers) => [
 *     {
 *       key: "code",
 *       label: "Código",
 *       required: true,
 *       render: (value) => <ClipboardTooltip value={value} />,
 *     },
 *     {
 *       key: "totalAmount",
 *       label: "Total",
 *       render: (value) => <Badge>{helpers.formatCurrency(value)}</Badge>,
 *     },
 *   ],
 *
 *   // Filtros opcionales
 *   filters: () => [
 *     {
 *       id: "status",
 *       name: "Estado",
 *       options: [
 *         { value: "all", label: "Todos" },
 *         { value: "pending", label: "Pendientes" },
 *       ],
 *       icon: "SquareCheck",
 *     },
 *   ],
 *
 *   // Custom hooks para lógica adicional (ej: PDF export)
 *   customHooks: (data, helpers) => {
 *     const [downloading, setDownloading] = useState(null);
 *     const pdfExport = useInvoicePDF(downloading);
 *
 *     useEffect(() => {
 *       if (downloading && pdfExport && !pdfExport.isDataLoading) {
 *         pdfExport.exportPDF().then(() => setDownloading(null));
 *       }
 *     }, [downloading, pdfExport]);
 *
 *     return {
 *       handleDownload: (invoice) => setDownloading(invoice),
 *     };
 *   },
 *
 *   // Acciones con acceso a customHooksData
 *   actions: (helpers, customHooksData) => ({
 *     onEdit: (invoice) => {
 *       helpers.navigate({ to: `/invoices/${invoice._id}/edit` });
 *     },
 *     onDelete: (invoice) => {
 *       helpers.deleteModal.open(invoice, true);
 *     },
 *     customActions: [
 *       {
 *         label: "Descargar PDF",
 *         icon: Download,
 *         onClick: customHooksData.handleDownload,
 *       },
 *     ],
 *   }),
 *
 *   // Modal integrado
 *   modal: {
 *     component: InvoicesModal,
 *     getItemName: (invoice) => invoice.code,
 *   },
 * });
 *
 * // Componente simple que usa el hook
 * export default function InvoicesTable({ currentPage, pageSize, basePath, searchParams }) {
 *   const table = useInvoicesTable({ currentPage, pageSize, bas;
 *   return table.render();
 * }
 */
export function createTableHook<TData extends Record<string, any>>(
  config: TableConfig<TData>,
) {
  return function useTable(options: UseTableOptions): UseTableReturn<TData> {
    const navigate = useNavigate();
    const { can } = usePermissions(config.tableName as any);

    // Fetch data
    const tableData = useTableData<TData>(
      {
        tableName: config.tableName,
        query: config.query,
        title:
          typeof config.title === "function" ? config.title() : config.title,
        description:
          typeof config.description === "function"
            ? config.description()
            : config.description,
      },
      options,
    );

    // Navigation
    const { navigateToPage, updateFilters, isPending } = useTableNavigation({
      basePath: options.basePath,
      currentPage: tableData.currentPage,
      nextPageUrl: tableData.nextPageUrl,
      previousPageUrl: tableData.previousPageUrl,
      resetFiltersUrl: tableData.resetFiltersUrl,
    });

    // Table data loaded successfully

    // Modals
    const modal = useModal<TData>();
    const deleteModal = useDeleteModal<TData>(
      config.modal?.getItemName ||
        ((item: TData) => {
          const itemRecord = item as Record<string, unknown>;
          return (
            (itemRecord?.code as string) ||
            (itemRecord?.name as string) ||
            (itemRecord?._id as string) ||
            "item"
          );
        }),
    );

    // Helpers
    const helpers: TableHelpers<TData> = {
      navigate,
      formatCurrency: (amount: number, currency: string = "HNL") => {
        return new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: currency,
        }).format(amount);
      },
      formatDate: (date: number | Date) => {
        const dateObj = typeof date === "number" ? new Date(date) : date;
        return dateObj.toLocaleDateString("es-ES");
      },
    };

    // Action helpers
    const actionHelpers: TableActionHelpers<TData> = {
      ...helpers,
      modal,
      deleteModal,
    };

    // Custom hooks
    const customHooksData: Record<string, any> = config.customHooks
      ? config.customHooks(tableData.data || [], helpers)
      : {};

    // Columns
    const columns = config.columns(helpers);

    // Filters
    const filters = config.filters
      ? config.filters(helpers, customHooksData)
      : [];

    // Actions
    const tableActions = config.actions
      ? config.actions(actionHelpers, customHooksData)
      : {};

    // Track delete state
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const getRowActions = (item: TData) => {
      const baseActions: Array<{
        id: string;
        label: string;
        icon: ReactNode;
        onClick: (row: TData) => void;
        variant?:
          | "default"
          | "destructive"
          | "outline"
          | "secondary"
          | "ghost"
          | "link";
      }> = [];

      if (tableActions.onView && can("read")) {
        baseActions.push({
          id: "view",
          label: "Ver",
          icon: <Eye />,
          onClick: () => tableActions.onView!(item),
        });
      }

      if (tableActions.onEdit && can("update")) {
        baseActions.push({
          id: "edit",
          label: "Editar",
          icon: <Edit />,
          onClick: () => tableActions.onEdit!(item),
        });
      }

      // Custom actions before delete
      if (tableActions.customActions) {
        tableActions.customActions.forEach((action, index) => {
          // Check condition if provided
          if (action.condition && !action.condition(item)) {
            return;
          }

          // Verificar permiso si está definido
          if (action.permission && !can(action.permission)) {
            return;
          }

          baseActions.push({
            id: `custom-${index}`,
            label: action.label,
            icon: action.icon ? <action.icon /> : null,
            onClick: () => action.onClick(item),
            variant: action.variant,
          });
        });
      }

      // Delete action at the end
      const canDelete =
        tableActions.onDelete &&
        can("delete") &&
        (!tableActions.deleteCondition || tableActions.deleteCondition(item));

      if (canDelete) {
        baseActions.push({
          id: "delete",
          label: "Eliminar",
          icon: <Trash className="" />,

          onClick: () => {
            if (deleteInProgress) return;

            setDeleteInProgress(true);

            // Call onDelete without skipModal flag - it should open modal if needed
            const result = tableActions.onDelete!(item);

            // If onDelete returns a promise, it executed mutation directly
            if (result instanceof Promise) {
              result.finally(() => {
                setDeleteInProgress(false);
              });
            } else {
              // onDelete returned void, assume it opened modal
              // Reset when modal closes
              const checkModal = () => {
                if (!deleteModal.isOpen) {
                  setDeleteInProgress(false);
                } else {
                  setTimeout(checkModal, 100);
                }
              };
              setTimeout(checkModal, 100);
            }
          },
          variant: "destructive" as const,
        });
      }

      return baseActions;
    };

    // Header actions
    const headerActions: HeaderAction[] = config.headerActions
      ? config.headerActions(customHooksData)
      : [];

    // Render helpers
    const renderTable = () => (
      <>
        {filters.length > 0 && (
          <TableFilters
            table={config.tableName}
            tableName={
              typeof config.title === "function" ? config.title() : config.title
            }
            filters={filters}
            updateFilters={updateFilters}
            setIsModalOpen={
              config.createAction === "modal" ? modal.create : undefined
            }
            rootPath={options.basePath}
            createRoute={config.createRoute}
            defaultPageSize={tableData.pageSize}
            headerActions={headerActions}
          />
        )}
        <DataTable<TData>
          title={
            typeof config.title === "function" ? config.title() : config.title
          }
          icon={config.icon}
          description={
            typeof config.description === "function"
              ? config.description()
              : config.description
          }
          columns={columns}
          rowActions={getRowActions}
          data={tableData.data || []}
          isLoading={tableData.isLoading || isPending}
          pagination={{
            totalCount: tableData.totalCount,
            totalCountIsLowerBound: tableData.totalCountIsLowerBound,
            currentPage: tableData.currentPage,
            onPageChange: navigateToPage,
            pageSize: tableData.pageSize,
          }}
          emptyState={{
            title: `No hay ${(typeof config.title === "function" ? config.title() : config.title).toLowerCase()}`,
            description: `Comienza creando ${(typeof config.title === "function" ? config.title() : config.title).toLowerCase()}.`,
          }}
          enableColumnVisibility={true}
          tableId={`${config.tableName}-table`}
          rowIdField="_id"
        />
      </>
    );

    const renderModals = () => {
      const ModalComponent = config.modal?.component;

      return (
        <>
          {ModalComponent && (
            <ModalComponent
              isOpen={modal.isOpen}
              data={modal.data}
              onClose={modal.close}
              onSuccess={modal.close}
            />
          )}
          <DeleteConfirmationModal
            isOpen={deleteModal.isOpen}
            isCritical={deleteModal.isCritical}
            itemName={
              deleteModal.data && config.modal?.getItemName
                ? config.modal.getItemName(deleteModal.data)
                : undefined
            }
            onClose={deleteModal.close}
            onConfirm={async () => {
              if (
                deleteModal.canConfirm &&
                deleteModal.data &&
                tableActions.onDelete
              ) {
                // Call onDelete with skipModal flag to indicate this is a confirmation
                const result = tableActions.onDelete(deleteModal.data, {
                  skipModal: true,
                });
                if (result instanceof Promise) {
                  await result;
                }
                setDeleteInProgress(false);
              }
              deleteModal.close();
            }}
            confirmationValue={deleteModal.confirmationValue}
            onConfirmationChange={deleteModal.setConfirmationValue}
            canConfirm={deleteModal.canConfirm}
          />
        </>
      );
    };

    const render = (options?: { extraContent?: ReactNode }) => (
      <div className="space-y-4">
        {renderTable()}
        {renderModals()}
        {options?.extraContent}
      </div>
    );

    return {
      // Data
      data: tableData.data || [],
      isLoading: tableData.isLoading,
      isError: tableData.isError,

      // Navigation
      currentPage: tableData.currentPage,
      pageSize: tableData.pageSize,
      totalCount: tableData.totalCount,
      totalPages: tableData.totalPages,

      // Modals
      modal,
      deleteModal,

      // Custom hooks
      customHooksData,

      // Render helpers
      renderTable,
      renderModals,
      render,
    };
  };
}
